import React from 'react';
import { Calendar, Users, FolderOpen, FileText, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { cn } from '@/lib/utils';

interface QuickFiltersProps {
  filters: TimesheetReportFilters;
  onFiltersChange: (filters: TimesheetReportFilters) => void;
  onAdvancedClick: () => void;
  availableEmployees: Array<{ id: string; name: string; department: string }>;
  availableProjects: Array<{ id: string; code: string; name: string }>;
  advancedFilterCount: number;
}

export const QuickFilters: React.FC<QuickFiltersProps> = ({
  filters,
  onFiltersChange,
  onAdvancedClick,
  availableEmployees,
  availableProjects,
  advancedFilterCount,
}) => {
  // Date range options
  const getDateRange = (range: string) => {
    const today = new Date();
    const currentDay = today.getDay();
    
    switch (range) {
      case 'this-week': {
        // Get Sunday of current week
        const sunday = new Date(today);
        sunday.setDate(today.getDate() - currentDay);
        sunday.setHours(0, 0, 0, 0);
        
        // Get Saturday of current week
        const saturday = new Date(sunday);
        saturday.setDate(sunday.getDate() + 6);
        saturday.setHours(23, 59, 59, 999);
        
        return { dateFrom: sunday, dateTo: saturday };
      }
      case 'last-week': {
        const sunday = new Date(today);
        sunday.setDate(today.getDate() - currentDay - 7);
        sunday.setHours(0, 0, 0, 0);
        
        const saturday = new Date(sunday);
        saturday.setDate(sunday.getDate() + 6);
        saturday.setHours(23, 59, 59, 999);
        
        return { dateFrom: sunday, dateTo: saturday };
      }
      case 'this-month': {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        lastDay.setHours(23, 59, 59, 999);
        
        return { dateFrom: firstDay, dateTo: lastDay };
      }
      case 'last-month': {
        const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
        lastDay.setHours(23, 59, 59, 999);
        
        return { dateFrom: firstDay, dateTo: lastDay };
      }
      default:
        return {};
    }
  };

  const handleDateRangeChange = (value: string) => {
    if (value === 'all') {
      const { dateFrom, dateTo, ...rest } = filters;
      onFiltersChange(rest);
    } else if (value === 'custom') {
      // Open advanced filters for custom date range
      onAdvancedClick();
    } else {
      const dateRange = getDateRange(value);
      onFiltersChange({ ...filters, ...dateRange, quickDateRange: value });
    }
  };

  const handleStatusChange = (status: string) => {
    const currentStatuses = filters.statuses || [];
    let newStatuses: typeof currentStatuses;
    
    if (status === 'all') {
      newStatuses = [];
    } else if (currentStatuses.includes(status as any)) {
      newStatuses = currentStatuses.filter(s => s !== status);
    } else {
      newStatuses = [...currentStatuses, status as any];
    }
    
    onFiltersChange({ ...filters, statuses: newStatuses.length > 0 ? newStatuses : undefined });
  };

  const handleEmployeeChange = (employeeId: string) => {
    if (employeeId === 'all') {
      const { employeeIds, ...rest } = filters;
      onFiltersChange(rest);
    } else {
      onFiltersChange({ ...filters, employeeIds: [employeeId] });
    }
  };

  const handleProjectChange = (projectId: string) => {
    if (projectId === 'all') {
      const { projectIds, ...rest } = filters;
      onFiltersChange(rest);
    } else {
      onFiltersChange({ ...filters, projectIds: [projectId] });
    }
  };

  // Get current date range value
  const getCurrentDateRange = () => {
    if (filters.quickDateRange) return filters.quickDateRange;
    if (!filters.dateFrom && !filters.dateTo) return 'all';
    return 'custom';
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border mb-4 flex-wrap">
      {/* Date Range */}
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-gray-500" />
        <Select
          value={getCurrentDateRange()}
          onValueChange={handleDateRangeChange}
        >
          <SelectTrigger className="w-[140px] h-9 bg-white">
            <SelectValue placeholder="Date Range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="this-week">This Week</SelectItem>
            <SelectItem value="last-week">Last Week</SelectItem>
            <SelectItem value="this-month">This Month</SelectItem>
            <SelectItem value="last-month">Last Month</SelectItem>
            <SelectItem value="custom">Custom Range...</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Status */}
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-gray-500" />
        <Select
          value={filters.statuses?.length === 1 ? filters.statuses[0] : filters.statuses?.length ? 'multiple' : 'all'}
          onValueChange={handleStatusChange}
        >
          <SelectTrigger className="w-[140px] h-9 bg-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="locked">Locked</SelectItem>
            {filters.statuses && filters.statuses.length > 1 && (
              <SelectItem value="multiple">Multiple ({filters.statuses.length})</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Employee */}
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-gray-500" />
        <Select
          value={filters.employeeIds?.[0] || 'all'}
          onValueChange={handleEmployeeChange}
        >
          <SelectTrigger className="w-[180px] h-9 bg-white">
            <SelectValue placeholder="Employee" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Employees</SelectItem>
            {availableEmployees.map(emp => (
              <SelectItem key={emp.id} value={emp.id}>
                {emp.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Project */}
      <div className="flex items-center gap-2">
        <FolderOpen className="h-4 w-4 text-gray-500" />
        <Select
          value={filters.projectIds?.[0] || 'all'}
          onValueChange={handleProjectChange}
        >
          <SelectTrigger className="w-[180px] h-9 bg-white">
            <SelectValue placeholder="Project" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {availableProjects.map(proj => (
              <SelectItem key={proj.id} value={proj.code}>
                {proj.code} - {proj.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Spacer */}
      <div className="flex-1 min-w-[20px]" />

      {/* Advanced Filters Button */}
      <Button
        variant="outline"
        onClick={onAdvancedClick}
        className="h-9 gap-2"
      >
        <FileText className="h-4 w-4" />
        Advanced Filters
        {advancedFilterCount > 0 && (
          <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
            {advancedFilterCount}
          </Badge>
        )}
      </Button>
    </div>
  );
};
